// src/env/index.ts
var _config = null;
function toCanonicalKey(key) {
  if (!key.startsWith("VITE_")) return key;
  const withoutPrefix = key.slice(5);
  return withoutPrefix.toLowerCase().replace(/_([a-z0-9])/g, (_, c) => c.toUpperCase());
}
function toLegacyKey(key) {
  const snake = key.replace(/([A-Z])/g, "_$1").toUpperCase();
  return `VITE_${snake}`;
}
async function loadConfig(url = "/config.json") {
  try {
    const res = await fetch(url, { cache: "no-store" });
    if (!res.ok) {
      console.warn(`[platform/env] GET ${url} \u2192 HTTP ${res.status}. Falling back to build-time env vars.`);
      return;
    }
    const data = await res.json();
    _config = data;
    if (typeof window !== "undefined") {
      const legacyEnv = {};
      for (const [k, v] of Object.entries(data)) {
        if (v == null) continue;
        legacyEnv[k] = v;
        legacyEnv[toLegacyKey(k)] = v;
      }
      window.__ENV__ = legacyEnv;
    }
  } catch (err) {
    console.warn("[platform/env] Failed to load config.json. Falling back to build-time env vars.", err);
  }
}
function getConfig() {
  return _config ?? {};
}
function getEnv(key, fallback) {
  if (_config !== null) {
    const canonical = toCanonicalKey(key);
    const fromConfig = _config[canonical] ?? _config[key];
    if (fromConfig != null) {
      const trimmed = fromConfig.trim();
      if (trimmed !== "") return trimmed;
    }
  }
  if (typeof window !== "undefined") {
    const winEnv = window.__ENV__;
    if (winEnv) {
      const canonical = toCanonicalKey(key);
      const fromWin = winEnv[canonical] ?? winEnv[key];
      if (fromWin != null) {
        const trimmed = fromWin.trim();
        if (trimmed !== "") return trimmed;
      }
    }
  }
  const metaImport = import.meta;
  const nodeEnv = globalThis.process?.env;
  const buildTime = metaImport.env?.[key] ?? nodeEnv?.[key];
  if (buildTime !== void 0 && buildTime !== "") return buildTime;
  return fallback;
}
function validateConfig(required) {
  const errors = [];
  for (const req of required) {
    const val = getEnv(req.key);
    if (!val || val.trim() === "") {
      errors.push({ ...req, canonicalKey: toCanonicalKey(req.key) });
    }
  }
  return errors;
}
function showBootstrapError(errors, appName) {
  const root = document.getElementById("root");
  if (!root) return;
  const label = appName ? `${appName} \u2014 ` : "";
  const items = errors.map((e) => `  <li><code>${e.canonicalKey}</code> \u2014 ${e.description}</li>`).join("\n");
  root.innerHTML = `
    <div style="font-family:var(--font-family-mono);padding:var(--space-8);max-width:40rem;margin:var(--space-8) auto;color:var(--color-text)">
      <h2 style="color:var(--signal-incident);margin:0 0 var(--space-1)">${label}Bootstrap Configuration Error</h2>
      <p style="margin:0 0 var(--space-4);color:var(--color-text-secondary)">The application could not start because required runtime configuration is missing.</p>
      <ul style="background:var(--signal-incident-bg);border:1px solid var(--signal-incident-bg-soft);border-radius:var(--radius-sm);padding:var(--space-4) var(--space-4) var(--space-4) var(--space-8);margin:0 0 var(--space-4);line-height:var(--line-height-relaxed)">
        ${items}
      </ul>
      <p style="margin:0;color:var(--color-text-secondary);font-size:var(--font-size-md)">
        Ensure <code>/config.json</code> is mounted correctly and contains all required keys.
        In local development, set the missing variables in <code>.env.local</code>.
      </p>
    </div>`;
}
var mode = import.meta.env?.MODE ?? globalThis.process?.env?.NODE_ENV ?? "development";
var isProduction = mode === "production";
var isDevelopment = mode === "development";
var isTest = mode === "test";
var isMockMode = getEnv("VITE_MOCK_MODE") === "true";
function isLoopbackAuthority(authority) {
  let hostname;
  try {
    hostname = new URL(authority).hostname.toLowerCase();
  } catch {
    return false;
  }
  return hostname === "localhost" || hostname === "127.0.0.1" || hostname === "::1" || hostname === "[::1]" || hostname === "0.0.0.0" || hostname.endsWith(".localhost");
}
function assertAuthorityUsable(authority, ctx) {
  if (!ctx.isProduction || ctx.isMockMode || !isLoopbackAuthority(authority)) return;
  throw new Error(
    `[platform/env] OIDC authority resolved to "${authority}" in a production build. A deployed SPA can never authenticate against loopback, so this is always a bug. Almost certainly the auth manager was constructed at module scope before \`await loadConfig()\` ran \u2014 check that nothing on the entry point's STATIC import graph reaches the module that calls resolveOidcAuthority(), and load it via \`await import(...)\` after loadConfig() instead. Otherwise, confirm /config.json is mounted and supplies keycloakUrl + keycloakRealm (or oidcAuthority). See NEXT-2096.`
  );
}
function resolveOidcAuthority(defaultAuthority = "http://localhost:8180/realms/smarttt", buildFlags = { isProduction, isMockMode }) {
  const keycloakUrl = getEnv("VITE_KEYCLOAK_URL");
  const keycloakRealm = getEnv("VITE_KEYCLOAK_REALM");
  const authority = keycloakUrl && keycloakRealm ? `${keycloakUrl}/realms/${keycloakRealm}` : getEnv("VITE_OIDC_AUTHORITY") ?? defaultAuthority;
  assertAuthorityUsable(authority, buildFlags);
  return authority;
}
function resolveFailClosedUrl(options, buildFlags = { isProduction, isMockMode }) {
  const value = getEnv(options.legacyKey);
  if (!buildFlags.isProduction || buildFlags.isMockMode) {
    return value && value.trim() !== "" ? value.trim() : options.devDefault;
  }
  const trimmed = value?.trim();
  if (!trimmed || isLoopbackAuthority(trimmed)) {
    throw new Error(
      `${options.logPrefix} ${options.canonicalKey} is missing or resolves to loopback ("${trimmed ?? ""}") in a production build. A deployed SPA can never route ${options.subject} to loopback, so this is always a bug: the Helm chart is not supplying this runtime config key. Confirm /config.json is mounted and supplies "${options.canonicalKey}" (or the legacy ${options.legacyKey} key). See ${options.ticket}.`
    );
  }
  return trimmed;
}

export {
  toCanonicalKey,
  toLegacyKey,
  loadConfig,
  getConfig,
  getEnv,
  validateConfig,
  showBootstrapError,
  isProduction,
  isDevelopment,
  isTest,
  isMockMode,
  resolveOidcAuthority,
  resolveFailClosedUrl
};
//# sourceMappingURL=chunk-TT3SLZOD.js.map